import type { ReactNode } from 'react';
import React, { useMemo } from 'react';
import type { FieldError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { ThemeColors } from '@theme/foundations/colors';

import type { TRules } from './types';
import { FormControl, FormHelperText } from '@chakra-ui/react';
import BEIcon from '@molecules/BEIcon';

export interface FormGroupProps {
  errorLabel?: string;
  name: string;
  label: string;
  error?: FieldError;
  required?: boolean;
  children: ReactNode;
  rules?: TRules;
  customValidationMessage?: string;
}

const isRuleType = (v: FieldError['type']): v is keyof TRules => {
  return !['valueAsNumber', 'valueAsDate', 'setValueAs'].includes(v);
};

export const FormGroup = ({
  children,
  errorLabel = '',
  label = '',
  error,
  required,
  rules,
  customValidationMessage,
}: FormGroupProps) => {
  const { t } = useTranslation('validation');
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const { message, type } = error! || {};

  const errorMessage = useMemo(() => {
    if (message) {
      return message;
    }

    if (isRuleType(type)) {
      const err =
        errorLabel || t(type, { label, value: rules?.[type] as string });

      return type ? err : undefined;
    }

    return undefined;
  }, [message, type, t, errorLabel, label, rules]);

  const helperText =
    (type === 'required' && customValidationMessage) || errorMessage;

  return (
    <FormControl isRequired={required}>
      {children}
      <StyledHelperText>
        {helperText && <BEIcon color="error" name="warn-circle" />}
        {helperText}
      </StyledHelperText>
    </FormControl>
  );
};

export const StyledHelperText = styled(FormHelperText)`
  display: flex;
  align-items: center;
  color: ${ThemeColors.error[600]};
`;

export default FormGroup;
